import { useState, useEffect, useCallback, useRef } from 'react';
import { Asset } from '../../state/types';
import { AssetFilters, ImageFilter } from '../../components/assets/config';
import { useSession } from '../../context/SessionContext';
import { useAssetApi } from '../../api/useAssetApi';
import { useSearchParams } from 'react-router-dom';
import {
  buildAssetsSearchParams,
  cloneDefaultAssetFilters,
  hasAssetShareParams,
  readAssetsInitialState,
  saveAssetsNavState,
} from './assetsStatePersistence';

export const useAssets = () => {
  const initialStateRef = useRef(readAssetsInitialState(window.location.search));
  const { selectedSystem, isLoading: sessionIsLoading, setIsLoading, assetFilterResetToken } = useSession();
  const { getAssetsApi } = useAssetApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [page, setPage] = useState(initialStateRef.current.page);
  const [filters, setFilters] = useState<AssetFilters>(initialStateRef.current.filters);
  const [resultsPerPage, setResultsPerPage] = useState(initialStateRef.current.resultsPerPage);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialStateRef.current.sortDirection);
  const [compactRows, setCompactRows] = useState(initialStateRef.current.compactRows);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);

  const lastSystemCodeRef = useRef<string | null>(selectedSystem?.systemCode ?? null);
  const isInitialResultsPerPage = useRef(true);
  const preserveUrlStateOnFirstSystemSync = useRef(hasAssetShareParams(window.location.search));

  useEffect(() => {
    // selectedSystem settles asynchronously on mount (null → loaded). Compare
    // codes so we only reset on a real user-driven switch between two distinct
    // systems, not on the initial null-to-loaded transition.
    const prev = lastSystemCodeRef.current;
    const current = selectedSystem?.systemCode ?? null;
    lastSystemCodeRef.current = current;

    if (preserveUrlStateOnFirstSystemSync.current) {
      preserveUrlStateOnFirstSystemSync.current = false;
      return;
    }

    if (!prev || !current || prev === current) return;

    setFilters(cloneDefaultAssetFilters());
    setPage(1);
  }, [selectedSystem]);

  useEffect(() => {
    if (isInitialResultsPerPage.current) {
      isInitialResultsPerPage.current = false;
      return;
    }
    setPage(1);
  }, [resultsPerPage]);

  const isInitialFilterReset = useRef(true);
  useEffect(() => {
    if (isInitialFilterReset.current) {
      isInitialFilterReset.current = false;
      return;
    }
    // User-initiated system switch — force a clean slate regardless of URL/mount-time guards.
    preserveUrlStateOnFirstSystemSync.current = false;
    setFilters(cloneDefaultAssetFilters());
    setPage(1);
  }, [assetFilterResetToken]);

  const fetchAssets = useCallback(async () => {
    if (sessionIsLoading || !selectedSystem) {
      return;
    }

    setIsLoading(true);
    if (!selectedSystem) {
      setAssets([]);
      setTotalAssets(0);
      setIsLoading(false);
      return;
    }
    try {
      const {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
        ...otherFilters
      } = filters;

      const payload: Record<string, any> = { ...otherFilters };

      // Asset ID lookups should bypass date filtering so older assets are still searchable.
      if (payload.asset_id) {
        delete payload.start_date;
        delete payload.end_date;
      }

      const imageFilters: Record<string, ImageFilter | undefined> = {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
      };

      for (const key in imageFilters) {
        const filter = imageFilters[key];
        if (filter?.checked) {
          payload[key] = {
            checked: true,
            operator: filter.operator || '=', // Default to '=' if not set
            imageCount: filter.imageCount ?? 0,   // Default to 0 if not set
          };
        }
      }

      const body = {
        ...payload,
        page,
        per_page: resultsPerPage,
        system_code: selectedSystem.systemCode,
        sort_direction: sortDirection,
      };
      const { assets: translatedAssets, total } = await getAssetsApi(body);
      setAssets(translatedAssets);
      setTotalAssets(total);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSystem, filters, page, resultsPerPage, sortDirection]);

  useEffect(() => {
    saveAssetsNavState({ page, filters, resultsPerPage, sortDirection, compactRows });
  }, [page, filters, resultsPerPage, sortDirection, compactRows]);

  useEffect(() => {
    const nextParams = buildAssetsSearchParams(
      searchParams,
      { page, filters, resultsPerPage, sortDirection, compactRows },
      selectedSystem?.systemCode
    );

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [compactRows, filters, page, resultsPerPage, searchParams, selectedSystem?.systemCode, setSearchParams, sortDirection]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchAssets();
    }, 150);
    return () => clearTimeout(debounceTimer);
  }, [fetchAssets]);

  const updateFilters = (newFilters: AssetFilters) => {
    setPage(1);
    setFilters(newFilters);
  };

  return {
    assets,
    totalAssets,
    page,
    setPage,
    resultsPerPage,
    setResultsPerPage,
    sortDirection,
    setSortDirection,
    compactRows,
    setCompactRows,
    filters,
    updateFilters,
    refetch: fetchAssets,
    selectedAssets,
    setSelectedAssets
  };
};
